import { BASE_URL } from '../../constants/baseUrl';
import axiosInstance from '../../utils/axiosInstance';
import logger from '../../utils/logger';

class BookingService {
  fetchBookings = async ({
    page = 1,
    pageSize = 10,
    search = '',
    bookingId = '',
    mobile = '',
    status = '',
    startDate = null,
    endDate = null,
    deliveryFilter = '',
    tripId = '',
    shipmentId = '',
  }) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('pageSize', pageSize);

    // Handle status filter - convert display text back to status code
    if (status) {
      const statusCode = this.getStatusCode(status);
      if (statusCode) params.set('status', statusCode);
    }

    // Handle date filters
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (bookingId) params.set('tripShipmentId', bookingId);
    if (mobile) params.set('mobileNumber', mobile);
    if (search) params.set('search', search);
    if (deliveryFilter) params.set('deliveryFilter', deliveryFilter);
    if (tripId) params.set('tripId', tripId);
    if (shipmentId) params.set('shipmentId', shipmentId);

    try {
      const res = await axiosInstance.get(
        `${BASE_URL}/v1/bookings/?${params.toString()}`,
      );

      const payload = res.data?.data || res.data || {};
      const bookings = payload.bookings || payload.result || [];
      const rows = bookings.map((booking) => this.mapBookingData(booking));

      // Support multiple API response formats for total count
      const total =
        payload.totalCount ??
        payload.total_count ??
        payload.totalRecords ??
        payload.total_records ??
        res.data?.totalCount ??
        res.data?.total_records ??
        0;

      return {
        rows,
        total: Number(total) || 0,
        page: payload.page || page,
        pageSize: payload.pageSize || pageSize,
      };
    } catch (error) {
      logger.error('Error fetching bookings:', error);
      return {
        rows: [],
        total: 0,
        page,
        pageSize,
      };
    }
  };

  // Map status code to display text
  mapStatus = (statusCode) => {
    const statusMap = {
      // CRT: 'Created',
      ASG: 'Assigned',
      PKP: 'Pickup',
      INT: 'In Transit',
      DLD: 'Delivered',
      // CLD: 'Canceled',
      RFP: 'Ready for Pickup',
      // ICR: 'Incident Reported',
      // NTY: 'Notify',
    };
    return statusMap[statusCode] || statusCode;
  };

  // Map display text back to status code
  getStatusCode = (displayText) => {
    const codeMap = {
      // Created: 'CRT',
      Assigned: 'ASG',
      Pickup: 'PKP',
      'In Transit': 'INT',
      Delivered: 'DLD',
      // Canceled: 'CLD',
      'Ready for Pickup': 'RFP',
      // 'Incident Reported': 'ICR',
      // Notify: 'NTY',
    };
    return codeMap[displayText];
  };
  getRatingByType = (ratings, type) => {
    return ratings.find((rating) => rating.ratingType === type);
  };
  getReviewByType = (ratings, type) => {
    return ratings.find((rating) => rating.ratingType === type)?.review || '';
  };

  mapBookingData = (booking) => {
    const trip = booking.trip || {};
    const tripUser = trip.user || {};
    const shipment = booking.shipment || {};
    const shipmentUser = shipment.ShipmentUser || {};
    const fare = booking.fare || {};
    const payout = booking.payout || null;

    // Map fare details from backend
    const baseFare = fare.baseFare || 0;
    const discount = fare.discount || 0;
    const originalFare = fare.originalFare || 0;
    const sgst = fare.sgst || 0;
    const cgst = fare.cgst || 0;
    const sgstPercentage = fare.sgstPercentage || '9.0 %';
    const cgstPercentage = fare.cgstPercentage || '9.0 %';
    const transactionCharge = fare.transactionCharge || 0;
    const totalFare = fare.totalFare || 0;
    const commission = fare.commission || 0;


    const ratings = booking.ratings || [];
    const travelerRatingObj = this.getRatingByType(ratings, 'from_traveler');
    const shipmentOwnerRatingObj = this.getRatingByType(
      ratings,
      'from_shipment_owner',
    );

    // Support multiple API field names for review text
    const getReviewText = (obj) =>
      (obj && (obj.review ?? obj.reviewText ?? obj.comment ?? obj.feedback ?? obj.description ?? '')) || '';


    return {
      id: booking.tripShipmentId,
      bookingId: booking.tripShipmentId,
      tripId: trip.tripId || booking.tripId || 'N/A',
      shipmentId: booking.shipmentId || 'N/A',
      source: trip.sourceName || 'N/A',
      destination: trip.destName || 'N/A',
      bookingDateTime: booking.bookingDate || 'N/A',
      deliveredDate: booking.deliveredDate || 'N/A',
      amount: booking.amount || 0,
      status: this.mapStatus(booking.status),
      statusCode: booking.status || 'N/A',

      // Tab 1: Booking Info
      startDate: trip.startDate || 'N/A',
      endDate: trip.endDate || 'N/A',
      packageType: trip.pkgType || 'N/A',
      packageSizeWeight: trip.pkgWeight || 'N/A',
      // specialInstructions: 'Standard delivery',

      // Offer details
      offerCode: booking.offerCode || null,
      offerAmount: booking.offerAmount || null,

      senderDetails: {
        userId: shipmentUser.userId || 'N/A',
        name: shipmentUser.name || 'N/A',
        phone: shipmentUser.phone || 'N/A',
        email: shipmentUser.email || 'N/A',
        source: shipment.sourceName || 'N/A',
        destination: shipment.destName || 'N/A',
        createdOn: shipment.createdOn || 'N/A',
        updatedOn: shipment.updatedOn || 'N/A',
        exceptedDelivery: shipment.exceptedDelivery || 'N/A',
        rating: travelerRatingObj?.rating ?? 0,
        review: getReviewText(travelerRatingObj),
      },

      travelerDetails: {
        userId: tripUser.userId || 'N/A',
        name: tripUser.name || 'N/A',
        phone: tripUser.phone || 'N/A',
        email: tripUser.email || 'N/A',
        source: trip.sourceName || 'N/A',
        destination: trip.destName || 'N/A',
        startDate: trip.startDate || 'N/A',
        endDate: trip.endDate || 'N/A',
        vehicleType: trip.vehicle || 'N/A',
      },
      // Tab 4: Receiver Details
      /* receiverDetails: {
        name: 'N/A',
        phone: 'N/A',
        dropOffAddress: shipment.destName || trip.destName || 'N/A',
        deliveryVerification: 'OTP Verification',
      },*/

      // Tab 5: Tracking Data
      tracking: {
        checkpoints: this.generateTrackingCheckpoints(booking),
      },

      // Tab 6: Fare Details
      fareDetails: {
        baseFare: fare.baseFare || 0,
        discount: fare.discount || 0,
        originalFare: fare.originalFare || 0,
        sgst: fare.sgst || 0,
        cgst: fare.cgst || 0,
        sgstPercentage: fare.sgstPercentage || '9.0 %',
        cgstPercentage: fare.cgstPercentage || '9.0 %',
        transactionCharge,
        totalFare: fare.totalFare || 0,
        commission: fare.commission || 0,
        promoDiscountAmount: fare.promoDiscountAmount || 0,
        appliedPromoId: fare.appliedPromoId || 'N/A',
        promoCode: fare.promoCode || 'N/A',
      },

      paymentMethod: 'Cash on Delivery',

      // Payout details
      payoutDetails: payout
        ? {
          transactionId: payout.transactionId || null,
          amount: payout.amount || 0,
          status: payout.status || 'PENDING',
        }
        : {
          transactionId: null,
          amount: 0,
          status: 'PENDING',
        },

      tripDetails: trip,
      ratings,
      // Traveler rating = from_shipment_owner = sender's rating of traveler (for Traveler Details tab)
      travelerRating: {
        rating: shipmentOwnerRatingObj?.rating ?? 0,
        review: getReviewText(shipmentOwnerRatingObj),
      },
      timeline: booking.timeline || [],
    };
  };

  generateTrackingCheckpoints = (booking) => {
    const checkpoints = [];
    const currentStatus = booking.status;

    // Always add Created checkpoint
    checkpoints.push({
      status: 'CRT',
      timestamp:
        booking.shipment?.createdOn ||
        booking.bookingDate ||
        new Date().toISOString(),
      description: 'Booking created',
    });

    // Add checkpoints based on current status
    const statusFlow = ['ASG', 'PKP', 'INT', 'DLD'];
    const currentIndex = statusFlow.indexOf(currentStatus);

    if (currentIndex >= 0) {
      for (let i = 0; i <= currentIndex; i++) {
        checkpoints.push({
          status: statusFlow[i],
          timestamp: booking.bookingDate || new Date().toISOString(),
          description: this.mapStatus(statusFlow[i]),
        });
      }
    }

    // Handle special statuses
    if (currentStatus === 'CLD') {
      checkpoints.push({
        status: 'CLD',
        timestamp: booking.bookingDate || new Date().toISOString(),
        description: 'Booking canceled',
      });
    } else if (currentStatus === 'RFP') {
      checkpoints.push({
        status: 'RFP',
        timestamp: booking.bookingDate || new Date().toISOString(),
        description: 'Ready for pickup',
      });
    } else if (currentStatus === 'ICR') {
      checkpoints.push({
        status: 'ICR',
        timestamp: booking.bookingDate || new Date().toISOString(),
        description: 'Incident reported',
      });
    } else if (currentStatus === 'NTY') {
      checkpoints.push({
        status: 'NTY',
        timestamp: booking.bookingDate || new Date().toISOString(),
        description: 'Notify',
      });
    }

    return checkpoints;
  };

  getBookingDetails = async (id) => {
    try {
      const response = await axiosInstance.get(
        `${BASE_URL}/v1/bookings/?tripShipmentId=${id}&page=1&pageSize=10`,
      );

      const data = response.data?.data || {};
      const bookings = data.bookings || [];

      const booking =
        bookings.find((b) => b.tripShipmentId.toString() === id.toString()) ||
        bookings[0];

      if (!booking) {
        throw new Error('Booking not found');
      }

      return this.mapBookingData(booking);
    } catch (error) {
      logger.error('Error fetching booking details:', error);
      throw error;
    }
  };
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new BookingService();
